import { useEffect, useState } from 'react'
import { EmptyState, ErrorBanner } from '@/components/AppLayout'
import { useAsync } from '@/hooks/useAsync'
import {
  closeForm,
  deleteSubmission,
  getForm,
  getFormSessionRotatedAt,
  listSubmissions,
  publishForm,
  rotateFormSessionSecret,
  setFormPasscode,
  updateForm,
  type SubmissionWithPicks,
} from '@/lib/queries'
import { listAttendees } from '@/lib/queries'
import { SubmissionDetailCard } from '@/features/submissions/SubmissionDetailCard'
import { groupBySubmitter } from '@/lib/submissionPicks'
import { passcodeSchema } from '@/lib/validation/schemas'
import type { MatchmakingForm } from '@/types/database'
import { useEventContext } from './eventContext'

const STATUS_COPY: Record<MatchmakingForm['status'], string> = {
  draft: 'Not published yet. Attendees cannot reach it.',
  published: 'Open. Attendees can fill it in with the passcode.',
  closed: 'Closed. The link still works but no longer accepts answers.',
}

export function FormPage() {
  const { event } = useEventContext()
  const form = useAsync(() => getForm(event.id), [event.id])
  const submissions = useAsync(() => listSubmissions(event.id), [event.id])
  // Needed to turn a stored attendee id into a person on the detail card.
  const attendees = useAsync(() => listAttendees(event.id), [event.id])
  const [openSubmissionId, setOpenSubmissionId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const [maxSelections, setMaxSelections] = useState(5)
  const [title, setTitle] = useState('')
  const [introText, setIntroText] = useState('')
  const [closingMessage, setClosingMessage] = useState('')
  const [allowResubmission, setAllowResubmission] = useState(true)
  const [collectGender, setCollectGender] = useState(true)
  const [passcode, setPasscode] = useState('')
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const record = form.data

  useEffect(() => {
    if (!record) return
    setMaxSelections(record.max_selections)
    setTitle(record.title ?? '')
    setIntroText(record.intro_text ?? '')
    setClosingMessage(record.closing_message ?? '')
    setAllowResubmission(record.allow_resubmission)
    setCollectGender(record.collect_gender)
  }, [record])

  const publicUrl = record ? `${window.location.origin}/f/${record.public_slug}` : ''

  async function withBusy(action: () => Promise<void>, message: string) {
    setBusy(true)
    setError(null)
    setSaved(null)
    try {
      await action()
      setSaved(message)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Removing one entry — a rehearsal submission, a duplicate, or someone who
   * asked to be taken out.
   *
   * It deletes the entry, not the person: anyone with an earlier entry keeps it
   * and it takes over, which is what the notice reports. Match results are left
   * alone — they are still internally consistent, just older than the
   * submissions now, the same position as when someone submits after a run.
   */
  async function handleDeleteSubmission(submission: SubmissionWithPicks, entryCount: number) {
    const name = `${submission.first_name} ${submission.last_name ?? ''}`.trim()
    const consequence =
      entryCount > 1
        ? 'Their other entries stay, and the newest of those becomes the answer the matchmaker reads.'
        : 'It also frees them to fill the form in again, even if resubmission is switched off.'
    const confirmed = window.confirm(
      `Delete this submission from ${name} (${submission.submitted_matchmaking_id})? Its picks go too and this cannot be undone. ${consequence}`
    )
    if (!confirmed) return

    setDeletingId(submission.id)
    setError(null)
    setSaved(null)
    try {
      const result = await deleteSubmission(submission.id)
      if (openSubmissionId === submission.id) setOpenSubmissionId(null)
      submissions.reload()
      setSaved(
        `Deleted a submission from ${name}. ` +
          (result.remaining_for_submitter === 0
            ? 'They now have no answers, so they are out of the matchmaking.'
            : result.remaining_for_submitter === 1
              ? 'Their remaining entry is now the one that counts.'
              : `They have ${String(result.remaining_for_submitter)} entries left; the newest is the one that counts.`) +
          (result.results_stale
            ? ' Re-run matchmaking on the Results tab to bring the results into line.'
            : '')
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the submission')
    } finally {
      setDeletingId(null)
    }
  }

  if (form.loading) return <p className="page-status">Loading form…</p>

  if (record === null) {
    return (
      <div className="card">
        <EmptyState
          title="No form yet"
          description="This event has no matchmaking form record. Creating the event again will add one."
        />
      </div>
    )
  }

  const hasPasscode = record.passcode_set_at !== null
  const rows = submissions.data ?? []
  const openSubmission = rows.find((s) => s.id === openSubmissionId)

  // Submitting again adds an entry rather than replacing one, so a person can
  // hold several rows. Grouping them is how the table can say which entry the
  // matchmaker will actually read, and how many are competing for that job.
  const entriesBySubmitter = groupBySubmitter(rows)

  /** Where this entry sits among that person's, newest first. */
  function entryRank(submission: SubmissionWithPicks): { index: number; total: number } {
    const entries = entriesBySubmitter.get(submission.submitter_attendee_id) ?? [submission]
    return { index: entries.findIndex((s) => s.id === submission.id), total: entries.length }
  }

  const repeatSubmitters = [...entriesBySubmitter.values()].filter(
    (entries) => entries.length > 1
  ).length

  return (
    <div className="stack">
      {error !== null && <ErrorBanner message={error} />}
      {saved !== null && <p className="banner banner-success">{saved}</p>}

      <div className="card">
        <div className="card-header">
          <h2>Status</h2>
          <span
            className={
              record.status === 'published'
                ? 'badge badge-success'
                : record.status === 'closed'
                  ? 'badge badge-warning'
                  : 'badge'
            }
          >
            {record.status}
          </span>
        </div>
        <p className="field-hint">{STATUS_COPY[record.status]}</p>

        {record.status === 'published' && (
          <div className="field">
            <label className="field-label" htmlFor="public-link">
              Link for attendees
            </label>
            <input id="public-link" type="text" value={publicUrl} readOnly />
            <div className="row-tight">
              <button
                className="btn btn-sm"
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(publicUrl).then(() => {
                    setCopied(true)
                    setTimeout(() => {
                      setCopied(false)
                    }, 2000)
                  })
                }}
              >
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
          </div>
        )}

        <div className="row btn-group-stack">
          {record.status !== 'published' ? (
            <button
              className="btn btn-primary"
              type="button"
              disabled={busy || !hasPasscode}
              title={hasPasscode ? undefined : 'Set a passcode before publishing'}
              onClick={() =>
                void withBusy(async () => {
                  await publishForm(record.id)
                  form.reload()
                }, 'The form is now open.')
              }
            >
              Publish form
            </button>
          ) : (
            <button
              className="btn"
              type="button"
              disabled={busy}
              onClick={() =>
                void withBusy(async () => {
                  await closeForm(record.id)
                  form.reload()
                }, 'The form is now closed.')
              }
            >
              Close form
            </button>
          )}
        </div>

        {!hasPasscode && (
          <p className="banner banner-warning">
            Set a passcode below before publishing — the form cannot go live without one.
          </p>
        )}
      </div>

      <form
        className="card"
        onSubmit={(e) => {
          e.preventDefault()
          void withBusy(async () => {
            await updateForm(record.id, {
              max_selections: maxSelections,
              title: title.trim() === '' ? null : title.trim(),
              intro_text: introText.trim() === '' ? null : introText.trim(),
              closing_message: closingMessage.trim() === '' ? null : closingMessage.trim(),
              allow_resubmission: allowResubmission,
              collect_gender: collectGender,
            })
            form.reload()
          }, 'Form settings saved.')
        }}
      >
        <h2>Form settings</h2>

        <div className="field">
          <label className="field-label" htmlFor="max-selections">
            How many people can each person pick?
          </label>
          <input
            id="max-selections"
            type="number"
            min={1}
            max={25}
            value={maxSelections}
            onChange={(e) => {
              setMaxSelections(Number(e.target.value))
            }}
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="form-title">
            Heading
          </label>
          <input
            id="form-title"
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value)
            }}
            placeholder={event.name}
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="intro-text">
            Intro text
          </label>
          <textarea
            id="intro-text"
            value={introText}
            onChange={(e) => {
              setIntroText(e.target.value)
            }}
            placeholder="Pick the people you would like to see again."
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="closing-message">
            Thank-you message
          </label>
          <p className="field-hint">Shown after someone submits.</p>
          <textarea
            id="closing-message"
            value={closingMessage}
            onChange={(e) => {
              setClosingMessage(e.target.value)
            }}
          />
        </div>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={allowResubmission}
            onChange={(e) => {
              setAllowResubmission(e.target.checked)
            }}
          />
          Let people fill the form in more than once
        </label>
        <p className="field-hint">
          Each go is kept as its own entry rather than replacing the last, so you can see what
          changed. The matchmaker reads only the newest; delete the rest from the list below.
        </p>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={collectGender}
            onChange={(e) => {
              setCollectGender(e.target.checked)
            }}
          />
          Ask for gender, for the match-rate breakdown
        </label>
        <p className="field-hint">
          Answering is always optional for the attendee, and the answer is never shown to anyone
          they match with.
        </p>

        <div className="row btn-group-stack">
          <button className="btn btn-primary" type="submit" disabled={busy}>
            Save settings
          </button>
        </div>
      </form>

      <form
        className="card"
        onSubmit={(e) => {
          e.preventDefault()
          const parsed = passcodeSchema.safeParse(passcode)
          if (!parsed.success) {
            setError(parsed.error.issues[0]?.message ?? 'Invalid passcode')
            return
          }
          void withBusy(async () => {
            await setFormPasscode(record.id, parsed.data)
            setPasscode('')
            form.reload()
          }, 'Passcode set. Give it to attendees at the event.')
        }}
      >
        <h2>Passcode</h2>
        <p className="field-hint">
          {hasPasscode
            ? 'A passcode is set. Entering a new one replaces it immediately, and also signs out anyone part-way through the form — their answers are kept, but they will need the new passcode.'
            : 'Attendees type this to open the form. It is stored hashed and cannot be read back, so keep your own copy.'}
        </p>

        <div className="field">
          <label className="field-label" htmlFor="passcode">
            {hasPasscode ? 'New passcode' : 'Passcode'}
          </label>
          <input
            id="passcode"
            type="text"
            value={passcode}
            onChange={(e) => {
              setPasscode(e.target.value)
            }}
            autoComplete="off"
            placeholder="At least 6 characters"
          />
        </div>

        <div className="row btn-group-stack">
          <button className="btn btn-primary" type="submit" disabled={busy || passcode === ''}>
            {hasPasscode ? 'Replace passcode' : 'Set passcode'}
          </button>
        </div>
      </form>

      <SigningKeyCard formId={record.id} onError={setError} onDone={setSaved} />

      {openSubmission !== undefined && (
        <SubmissionDetailCard
          submission={openSubmission}
          allSubmissions={rows}
          attendees={attendees.data ?? []}
          isNewestForSubmitter={entryRank(openSubmission).index === 0}
          onClose={() => {
            setOpenSubmissionId(null)
          }}
        />
      )}

      <div className="card">
        <div className="card-header">
          <h2>Submissions</h2>
          <span className="badge">{rows.length}</span>
        </div>

        {repeatSubmitters > 0 && (
          <p className="banner banner-warning">
            {repeatSubmitters} {repeatSubmitters === 1 ? 'person' : 'people'} filled the form in
            more than once. Every answer is kept, but the matchmaker reads only each person&rsquo;s
            newest — marked <strong>counts</strong> below. Delete the entries that should not be
            there.
          </p>
        )}

        {submissions.loading ? (
          <p className="field-hint">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="field-hint">Nobody has filled the form in yet.</p>
        ) : (
          <div className="table-responsive">
            <div className="table-wrap">
              <table>
                <caption className="sr-only">Form submissions</caption>
                <thead>
                  <tr>
                    <th scope="col">No.</th>
                    <th scope="col">Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Picks</th>
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((submission) => {
                    const { index, total } = entryRank(submission)
                    const counts = index === 0

                    return (
                      <tr
                        key={submission.id}
                        // Superseded by a later entry: still real, still
                        // deletable, but not what anyone will be matched on.
                        className={counts ? undefined : 'row-inactive'}
                      >
                        <td data-label="No." className="cell-number">
                          {submission.submitted_matchmaking_id}
                        </td>
                        <td data-label="Name">
                          {submission.first_name} {submission.last_name ?? ''}
                          {total > 1 && (
                            <>
                              {' '}
                              <span
                                className={counts ? 'badge badge-success' : 'badge badge-warning'}
                                title={
                                  counts
                                    ? `The newest of ${String(total)} entries from this person, and the one the matchmaker reads`
                                    : `Entry ${String(total - index)} of ${String(total)}. A later one from this person is what counts.`
                                }
                              >
                                {counts ? 'counts' : 'superseded'}
                              </span>
                            </>
                          )}
                          {submission.comment !== null && (
                            <>
                              {' '}
                              <span className="badge badge-accent" title={submission.comment}>
                                note
                              </span>
                            </>
                          )}
                        </td>
                        <td data-label="Email" className="cell-muted">
                          {submission.email}
                        </td>
                        <td data-label="Picks">{submission.selections.length}</td>
                        <td data-label="">
                          <div className="row-tight">
                            <button
                              className="btn btn-sm"
                              type="button"
                              onClick={() => {
                                setOpenSubmissionId(
                                  openSubmissionId === submission.id ? null : submission.id
                                )
                              }}
                            >
                              {openSubmissionId === submission.id ? 'Hide picks' : 'View picks'}
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              type="button"
                              disabled={deletingId === submission.id}
                              onClick={() => void handleDeleteSubmission(submission, total)}
                            >
                              {deletingId === submission.id ? 'Deleting…' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The per-form session signing key.
 *
 * Each form has its own key, so rotating one event's key leaves every other
 * event alone. The value is never sent to the browser — an admin has no use for
 * it, and this page only ever shows when it last changed.
 */
function SigningKeyCard({
  formId,
  onError,
  onDone,
}: {
  formId: string
  onError: (message: string | null) => void
  onDone: (message: string | null) => void
}) {
  const rotatedAt = useAsync(() => getFormSessionRotatedAt(formId), [formId])
  const [busy, setBusy] = useState(false)

  async function handleRotate() {
    const confirmed = window.confirm(
      'Sign out everyone currently filling in the form? They will need to enter the passcode again. Their answers are kept.'
    )
    if (!confirmed) return

    setBusy(true)
    onError(null)
    onDone(null)
    try {
      await rotateFormSessionSecret(formId)
      rotatedAt.reload()
      onDone('Open form sessions have been signed out.')
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not rotate the signing key')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <h2>Open form sessions</h2>
      <p className="field-hint">
        Entering the passcode gives someone a session that lasts 45 minutes. Signing them out ends
        every open session for this event at once — useful if the passcode has been shared more
        widely than you intended. It affects no other event.
      </p>
      {rotatedAt.data !== null && (
        <p className="field-hint">Last signed out: {new Date(rotatedAt.data).toLocaleString()}.</p>
      )}
      <div className="row btn-group-stack">
        <button className="btn" type="button" onClick={() => void handleRotate()} disabled={busy}>
          {busy ? 'Signing out…' : 'Sign out all open sessions'}
        </button>
      </div>
    </div>
  )
}
